import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  performanceApi,
  type PerformanceCampaignListResponse,
  type PerformanceCampaignSortField,
  type PerformanceSortDir,
  ApiError,
} from '@/services/api';
import { usePerformanceContext } from '../../PerformanceContext';

interface State {
  data: PerformanceCampaignListResponse | null;
  loading: boolean;
  error: string | null;
}

const PAGE_SIZE = 50;

export function useCampaignList() {
  const { clientUid, dateFrom, dateTo } = usePerformanceContext();
  const [searchParams] = useSearchParams();

  const objective = searchParams.get('objective');
  const search = searchParams.get('search');
  const sortField = (searchParams.get('sort_field') ?? 'spend') as PerformanceCampaignSortField;
  const sortDir = (searchParams.get('sort_dir') ?? 'desc') as PerformanceSortDir;
  const page = Math.max(0, Number(searchParams.get('page') ?? '0'));

  const [state, setState] = useState<State>({ data: null, loading: false, error: null });

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    performanceApi
      .getCampaignList({
        clientUid,
        dateFrom,
        dateTo,
        objective,
        search,
        sortField,
        sortDir,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.body : String(err);
        setState({ data: null, loading: false, error: msg });
      });

    return () => {
      cancelled = true;
    };
  }, [clientUid, dateFrom, dateTo, objective, search, sortField, sortDir, page]);

  return { ...state, page, pageSize: PAGE_SIZE, sortField, sortDir };
}
